import { useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

function api(path, token, options = {}) {
  return fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    ...options,
  }).then((r) => r.json());
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function ticketLabel(type, count) {
  const t = type === 'DIGITAL' ? '디지털' : '실물';
  return `${t} ${count}회권`;
}

function statusBadge(status) {
  if (status === 'REFUNDED') return <span className="badge badge-refunded">환불됨</span>;
  return <span className="badge badge-completed">완료</span>;
}

export default function Payments({ token }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [lastKey, setLastKey] = useState(null);
  const [fetched, setFetched] = useState(false);

  // Detail view
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refunding, setRefunding] = useState(false);

  const fetchPayments = useCallback(async (append = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (append && lastKey) params.set('lastKey', lastKey);
      params.set('limit', '20');

      const data = await api(`/admin/payments?${params}`, token);
      if (append) {
        setPayments((prev) => [...prev, ...(data.payments || [])]);
      } else {
        setPayments(data.payments || []);
      }
      setLastKey(data.lastKey || null);
      setFetched(true);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [search, lastKey, token]);

  const handleSearch = (e) => {
    e.preventDefault();
    setLastKey(null);
    fetchPayments(false);
  };

  const openDetail = async (userId, paymentId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await api(`/admin/payments/${userId}/${paymentId}`, token);
      setDetail(data);
    } catch (e) {
      console.error(e);
      alert('상세 정보를 불러오는데 실패했습니다.');
    }
    setDetailLoading(false);
  };

  const handleRefund = async () => {
    if (!detail) return;
    const { userId, paymentId } = detail.payment;
    if (!window.confirm(`정말 환불하시겠습니까?\n\n${ticketLabel(detail.refund.ticketType, detail.refund.purchasedCount)} 차감 후 환불 처리됩니다.`)) return;

    setRefunding(true);
    try {
      const res = await api(`/admin/payments/${userId}/${paymentId}/refund`, token, { method: 'POST' });
      if (res.error) {
        alert(`환불 실패: ${res.error}`);
      } else {
        alert('환불이 완료되었습니다.');
        // Refresh detail and list
        openDetail(userId, paymentId);
        fetchPayments(false);
      }
    } catch (e) {
      alert('환불 처리 중 오류가 발생했습니다.');
    }
    setRefunding(false);
  };

  // Detail view
  if (detail || detailLoading) {
    return (
      <div className="payments-page">
        <button className="back-btn" onClick={() => setDetail(null)}>
          &larr; 목록으로
        </button>

        {detailLoading ? (
          <p style={{ padding: 24 }}>로딩 중...</p>
        ) : detail && (
          <div className="detail-container">
            <div className="detail-card">
              <h3>거래 정보</h3>
              <div className="detail-grid">
                <div className="detail-row"><label>거래 ID</label><span>{detail.payment.paymentId}</span></div>
                <div className="detail-row"><label>주문 ID</label><span>{detail.payment.orderId}</span></div>
                <div className="detail-row"><label>사용자 ID</label><span>{detail.payment.userId}</span></div>
                <div className="detail-row"><label>상품</label><span>{ticketLabel(detail.refund.ticketType, detail.refund.purchasedCount)}</span></div>
                <div className="detail-row"><label>SKU</label><span style={{ fontSize: 12, wordBreak: 'break-all' }}>{detail.payment.sku}</span></div>
                <div className="detail-row"><label>상태</label><span>{statusBadge(detail.payment.status)}</span></div>
                <div className="detail-row"><label>결제일시</label><span>{formatDate(detail.payment.createdAt)}</span></div>
                {detail.payment.refundedAt && (
                  <div className="detail-row"><label>환불일시</label><span>{formatDate(detail.payment.refundedAt)}</span></div>
                )}
              </div>
            </div>

            <div className="detail-card">
              <h3>사용자 발송권 현황</h3>
              <div className="detail-grid">
                <div className="detail-row"><label>사용자명</label><span>{detail.user.name}</span></div>
                <div className="detail-row"><label>디지털 발송권</label><span className="ticket-count">{detail.user.digitalTickets}장</span></div>
                <div className="detail-row"><label>실물 발송권</label><span className="ticket-count">{detail.user.physicalTickets}장</span></div>
              </div>
            </div>

            <div className="detail-card">
              <h3>환불 가능 여부</h3>
              <p className="refund-notice">앱인토스 콘솔에서 환불이 확정되면 아래 환불기능을 활용하세요.</p>
              <div className="detail-grid">
                <div className="detail-row">
                  <label>환불 가능</label>
                  <span className={detail.refund.refundable ? 'refund-ok' : 'refund-no'}>
                    {detail.refund.refundable ? '가능' : '불가'}
                  </span>
                </div>
                <div className="detail-row"><label>결제 경과일</label><span>{detail.refund.daysSincePurchase}일</span></div>
                <div className="detail-row"><label>7일 이내</label><span>{detail.refund.isWithin7Days ? '예' : '아니오'}</span></div>
                <div className="detail-row"><label>묶음 일부 사용</label><span>{detail.refund.isBundlePartiallyUsed ? '예' : '아니오'}</span></div>
                <div className="detail-row"><label>구매 수량</label><span>{detail.refund.purchasedCount}장</span></div>
                <div className="detail-row"><label>현재 잔액</label><span>{detail.refund.currentBalance}장</span></div>
                {!detail.refund.refundable && detail.refund.refundBlockReason && (
                  <div className="detail-row reason-row">
                    <label>사유</label>
                    <span className="refund-reason">{detail.refund.refundBlockReason}</span>
                  </div>
                )}
              </div>

              {detail.refund.refundable && (
                <button
                  className="refund-btn"
                  onClick={handleRefund}
                  disabled={refunding}
                >
                  {refunding ? '처리 중...' : '환불 처리'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="payments-page">
      <form className="search-bar" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="거래 ID 또는 사용자 ID로 검색..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" disabled={loading}>검색</button>
        {search && (
          <button type="button" onClick={() => { setSearch(''); setLastKey(null); setPayments([]); setFetched(false); }}>
            초기화
          </button>
        )}
      </form>

      {!fetched && !loading && (
        <div className="empty-state">
          <p>검색하거나 아래 버튼을 눌러 거래 내역을 불러오세요.</p>
          <button className="load-btn" onClick={() => fetchPayments(false)}>전체 거래 불러오기</button>
        </div>
      )}

      {loading && payments.length === 0 && <p style={{ padding: 24 }}>로딩 중...</p>}

      {payments.length > 0 && (
        <>
          <div className="payment-table-wrap">
            <table className="payment-table">
              <thead>
                <tr>
                  <th>결제일시</th>
                  <th>사용자 ID</th>
                  <th>주문 ID</th>
                  <th>상품</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.SK} onClick={() => openDetail(p.userId, p.paymentId)} className="clickable-row">
                    <td>{formatDate(p.createdAt)}</td>
                    <td className="id-cell">{p.userId?.slice(0, 12)}...</td>
                    <td className="id-cell">{p.orderId?.slice(0, 16)}...</td>
                    <td>{ticketLabel(p.ticketType, p.ticketCount)}</td>
                    <td>{statusBadge(p.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {lastKey && (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <button className="load-more-btn" onClick={() => fetchPayments(true)} disabled={loading}>
                {loading ? '로딩 중...' : '더 보기'}
              </button>
            </div>
          )}
        </>
      )}

      {fetched && !loading && payments.length === 0 && (
        <div className="empty-state">
          <p>검색 결과가 없습니다.</p>
        </div>
      )}
    </div>
  );
}
